import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Package 0 Safety Freeze — central feature-mode hook.
 *
 * Fetches /api/feature-mode once per browser session, caches, and returns
 * the same flags every UI component uses to decide whether to render
 * payment / delivery / donor-communication controls.
 *
 * NEVER add ad-hoc environment reads in components — go through this hook.
 */

const CACHE_KEY = "__engage_feature_mode_cache__";

const SAFE_DEFAULT = {
  secureExportMvp: true,
  financialExecutionMode: "statements_only",
  financialExecutionAllowed: false,
  externalDonorDeliveryAllowed: false,
  platformDonorCommsAllowed: false,
  donationPlatformFallbackEnabled: false,
  __loaded: false,
};

export function useFeatureMode() {
  const [mode, setMode] = useState(() => {
    if (typeof window !== "undefined" && window[CACHE_KEY]) {
      return window[CACHE_KEY];
    }
    return SAFE_DEFAULT;
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window[CACHE_KEY]?.__loaded) {
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios
      .get(`${base}/api/feature-mode`, { timeout: 6000 })
      .then((r) => {
        const next = { ...SAFE_DEFAULT, ...(r.data || {}), __loaded: true };
        if (typeof window !== "undefined") window[CACHE_KEY] = next;
        setMode(next);
      })
      .catch(() => {
        // Fail closed. Leave SAFE_DEFAULT in place.
      });
  }, []);

  return mode;
}

export function isFinancialExecutionAllowed(mode) {
  return !!(mode && mode.financialExecutionAllowed);
}

export function isExternalDonorDeliveryAllowed(mode) {
  return !!(mode && mode.externalDonorDeliveryAllowed);
}

export function isPlatformDonorCommunicationAllowed(mode) {
  return !!(mode && mode.platformDonorCommsAllowed);
}

export function isSecureExportMvpEnabled(mode) {
  return !!(mode && mode.secureExportMvp);
}
